import { SudoPlatformLogo } from 'admin-web-ui'
import { logIn } from 'admin-web-utils'
import { CognitoUser } from 'amazon-cognito-identity-js'
import React, { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { Heading, SimplePage, SubHeading } from '../../components/SimplePage'
import { useAppContext } from '../../containers/App'
import { completeNewPasswordChallenge } from '../../utils/cognito'
import { parseQueryParams } from '../../utils/url'
import useAsyncFnThrowing from '../../utils/use-async-fn-throwing'
import { FormData, LoginForm } from './LoginForm'
import { SetPasswordForm } from './SetPasswordForm'

const Content = styled.div`
  width: 368px;
  @media screen and (max-width: 368px) {
    width: 300px;
  }
`

const StyledSudoPlatformLogo = styled(SudoPlatformLogo)`
  margin-bottom: 24px;
`

type Step = 'login' | 'setInitialPassword'

export const Login: React.FC = () => {
  const location = useLocation()
  const { ret } = parseQueryParams(location.search)
  const navigate = useNavigate()
  const { cognitoUserPool, setCognitoUser } = useAppContext()

  const handleSuccess = useCallback(async () => {
    navigate(ret || '/')
  }, [navigate, ret])

  const [loginState, login] = useAsyncFnThrowing(
    async (formData?: FormData) => {
      if (!formData) {
        return undefined
      }

      const result = await logIn({
        cognitoUser: new CognitoUser({
          Pool: cognitoUserPool,
          Username: formData.email,
        }),
        password: formData.password,
        onCognitoUserChanged: setCognitoUser,
      })

      if (result.type !== 'newPasswordRequired') {
        handleSuccess()
      }

      return result
    },
    [cognitoUserPool, handleSuccess],
  )

  const submitLogin = useCallback(
    async (values?: FormData) => {
      await login(values)
    },
    [login],
  )

  const [, submitSetPassword] = useAsyncFnThrowing(
    async (newPassword?: string) => {
      if (loginState.value?.type !== 'newPasswordRequired') {
        throw new Error(
          "Unexpected. Login result should be 'newPasswordRequired'",
        )
      } else if (!newPassword) {
        return undefined
      }

      await completeNewPasswordChallenge({
        cognitoUser: loginState.value.user,
        newPassword,
        onCognitoUserChanged: setCognitoUser,
      })

      handleSuccess()
    },
    [loginState, handleSuccess, setCognitoUser],
  )

  const resetSubmitErrors = useCallback(() => {
    login(undefined)
    submitSetPassword(undefined)
  }, [login, submitSetPassword])

  const step: Step =
    loginState.value?.type === 'newPasswordRequired'
      ? 'setInitialPassword'
      : 'login'

  return (
    <SimplePage>
      <Content>
        <StyledSudoPlatformLogo width={227} />
        <Heading>Login</Heading>

        {step === 'login' && (
          <>
            <SubHeading>Welcome back</SubHeading>
            <LoginForm onSubmit={submitLogin} />
          </>
        )}

        {step === 'setInitialPassword' && (
          <>
            <SubHeading>Set your password</SubHeading>
            <SetPasswordForm
              onSubmit={submitSetPassword}
              onCancel={resetSubmitErrors}
            />
          </>
        )}
      </Content>
    </SimplePage>
  )
}
